/*
 * reviews-drawer.js
 * ---------------------------------------------------------------------------
 * Controller for the slide-in reviews drawer (snippets/reviews-drawer.liquid,
 * rendered once sitewide from layout/theme.liquid).
 * Same mechanics as faq-drawer.js: a single native <dialog class="reviews-drawer">
 * per page, opened by any element carrying [data-reviews-open]; slides in from
 * the right; animated, scroll-locking close on the button, ESC and backdrop
 * click; focus moves into the panel on open and returns to the trigger on close.
 *
 * The Yotpo widget is NOT in the DOM at page load — its markup sits inert in a
 * <template data-reviews-widget> so Yotpo's loader can't initialize it inside
 * the hidden dialog (display:none breaks its layout math, and it would fetch
 * reviews nobody asked for). On first open we mount the div and call
 * yotpoWidgetsContainer.initWidgets() — Yotpo's documented API for widgets
 * added after page load. If the loader hasn't booted yet, its own initial DOM
 * scan picks the mounted div up instead.
 * ---------------------------------------------------------------------------
 */
(function () {
  var dialog = document.querySelector('dialog.reviews-drawer');
  if (!dialog || dialog.dataset.reviewsInit) return;
  dialog.dataset.reviewsInit = '1';

  var CLOSE_MS = 450; // keep in sync with the transform transition in reviews-drawer.css
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var mount = dialog.querySelector('[data-reviews-mount]');
  var tpl = document.querySelector('template[data-reviews-widget]');
  var closeBtn = dialog.querySelector('.reviews-drawer__close');

  var lastTrigger = null;
  var closing = false;
  var closeTimer = null;
  var mountedProductId = null;
  // Live MutationObserver while one of Yotpo's teleported pop-ups is up (see the pop-ups block below).
  var popupWatch = null;
  // Live MutationObserver while a freshly mounted widget is still waiting on Yotpo (watchLoading()).
  var loadingWatch = null;

  // A trigger may name the product whose reviews it wants (snippets/reviews-trigger.liquid).
  // The <template> is resolved server-side from the page's own product, which is right on a
  // PDP but always the default product everywhere else — so on the homepage every star row
  // opened the same (toilet-paper) reviews. Map the trigger's dataset onto the Yotpo div's
  // own attribute names.
  //
  // A trigger that omits one gets NO attribute, not the template's — inheriting one
  // product's name/image onto another product's widget sends Yotpo wrong data, which is
  // worse than sending it nothing, and "" is not "nothing": it is a value Yotpo can write
  // back over the real one (the ac8c178 rule for the description — absent means "no data",
  // "" does not). A product with no featured image emits no image attribute at all
  // (snippets/reviews-trigger.liquid), so this is the path that carries that through.
  var PRODUCT_ATTRS = {
    reviewsProductId: 'data-yotpo-product-id',
    reviewsProductName: 'data-yotpo-name',
    reviewsProductUrl: 'data-yotpo-url',
    reviewsProductImage: 'data-yotpo-image-url'
  };

  // `data-yotpo-description` is NOT in that map: no trigger carries one (the full product
  // description on every star row was real page weight), so mapping it would have written
  // an empty string over the template's — and an empty description is a value Yotpo can
  // write back into its catalog, clearing the real one. Handled by DESCRIPTION_ATTR below:
  // the template's copy is kept only when the mounted product IS the template's product,
  // and the attribute is dropped entirely otherwise. Absent means "no data"; "" does not.
  var DESCRIPTION_ATTR = 'data-yotpo-description';

  // The template's own product. A trigger that names no product — the homepage marquee's
  // "Read our reviews", say — wants THIS, not whatever a previous open happened to leave
  // mounted. Read once: the template is inert and never rewritten.
  var tplWidget = tpl ? tpl.content.querySelector('.yotpo-widget-instance') : null;
  var defaultProductId = tplWidget ? tplWidget.getAttribute('data-yotpo-product-id') : null;

  function mountWidget(trigger) {
    if (!mount || !tpl) return;
    var named = (trigger && trigger.dataset && trigger.dataset.reviewsProductId) || null;
    var wanted = named || defaultProductId;
    // Already showing this product — don't tear down a widget that has fetched its reviews.
    // NB: mountedProductId survives a bfcache restore, so this must compare the resolved
    // product, never "did the trigger name one".
    if (mountedProductId !== null && wanted === mountedProductId) return;

    var frag = tpl.content.cloneNode(true);
    var widget = frag.querySelector('.yotpo-widget-instance');
    if (widget) {
      if (named) {
        Object.keys(PRODUCT_ATTRS).forEach(function (key) {
          var value = trigger.dataset[key];
          if (value) widget.setAttribute(PRODUCT_ATTRS[key], value);
          else widget.removeAttribute(PRODUCT_ATTRS[key]);
        });
      }
      // The template's description describes the template's product. Keep it only when
      // that is what we are mounting.
      if (wanted !== defaultProductId) widget.removeAttribute(DESCRIPTION_ATTR);
    }
    mount.textContent = '';
    mount.appendChild(frag);
    mountedProductId = wanted || (widget ? widget.getAttribute('data-yotpo-product-id') : '');
    if (widget) watchLoading(widget);

    if (window.yotpoWidgetsContainer &&
        typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
      // initWidgets() re-renders EVERY Yotpo widget on the page (stars, carousels), not just
      // this one — fine once per mount, never call it on a loop or a timer.
      // Never let a Yotpo error bubble out of the open path — the div is already
      // mounted, so the loader's own scan can still pick it up; a throw here would
      // leave a repurposed "See more reviews" link dead (default already suppressed).
      try {
        window.yotpoWidgetsContainer.initWidgets();
      } catch (e) { /* loader present but initWidgets threw — ignore */ }
    }
  }

  // The "Loading reviews…" hint (reviews-drawer.css) hangs off [data-reviews-loading]. It used to
  // be :empty, but Yotpo inserts its layout the moment the widget initialises and only fills it
  // once its reviews API answers, so :empty let go long before there was anything to read and a
  // slow answer showed a blank panel (client-reported 2026-09-24). Keep the hint until the widget
  // has text of its own — reviews, Yotpo's "no reviews yet" and an error message all count, so it
  // cannot outstay whatever Yotpo ends up showing. While waiting, the layout has no text at all.
  function watchLoading(widget) {
    if (loadingWatch) loadingWatch.disconnect();
    widget.setAttribute('data-reviews-loading', '');
    loadingWatch = new window.MutationObserver(function () {
      if (!widget.textContent.trim()) return;
      widget.removeAttribute('data-reviews-loading');
      loadingWatch.disconnect();
      loadingWatch = null;
    });
    loadingWatch.observe(widget, { childList: true, subtree: true, characterData: true });
  }

  function openDrawer(trigger) {
    // Any pending pop-up watch belongs to the drawer state we are leaving behind.
    stopPopupWatch();
    // A close animation may still be pending (reopened within CLOSE_MS) — cancel it
    // so its finishClose() can't slam the freshly reopened drawer shut.
    if (closeTimer) { window.clearTimeout(closeTimer); closeTimer = null; }
    closing = false;
    lastTrigger = trigger || null;
    mountWidget(trigger);

    // Triggered from inside the cart drawer (the upsell card)? Close it first.
    // cart-drawer.js runs trapFocus() on .drawer__inner, and that trap's global
    // focusin handler would yank focus straight back out of this dialog. Closing
    // releases the trap and its own body.overflow-hidden lock, which we re-add below.
    var cartDrawer = trigger && trigger.closest ? trigger.closest('cart-drawer') : null;
    if (cartDrawer && typeof cartDrawer.close === 'function') {
      cartDrawer.close();
      // Don't return focus into the now-hidden cart drawer on close.
      lastTrigger = null;
    }

    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    // Lock background scroll with the theme's shared helper (base.css
    // .overflow-hidden) — same as faq-drawer.js / cart-drawer.js.
    document.body.classList.add('overflow-hidden');

    if (reduceMotion) {
      dialog.classList.add('is-open');
    } else {
      // next frame so the transform transition runs from the parked position
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { dialog.classList.add('is-open'); });
      });
    }
    if (closeBtn) closeBtn.focus();
  }

  function finishClose() {
    if (dialog.open && typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    closing = false;
    closeTimer = null;
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
    lastTrigger = null;
  }

  function closeDrawer() {
    if (closing) return;
    closing = true;
    dialog.classList.remove('is-open');
    if (reduceMotion) finishClose();
    else closeTimer = window.setTimeout(finishClose, CLOSE_MS);
  }

  // ---- Wiring ----

  // Any trigger anywhere on the page (summary card now; reusable by future
  // "read all reviews" links — just add data-reviews-open to the element).
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-reviews-open]');
    if (!trigger) return;
    event.preventDefault();
    openDrawer(trigger);
  });

  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

  // Backdrop click: the dialog element itself receives clicks on its ::backdrop.
  dialog.addEventListener('click', function (event) {
    if (event.target === dialog) closeDrawer();
  });

  // Yotpo's "Write a review" form is teleported to <body>; the modal dialog's
  // top layer would cover it, so close the drawer when the button is clicked.
  dialog.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest('.yotpo-new-review-btn')) return;
    lastTrigger = null;
    closeDrawer();
  }, true);

  // ---- Yotpo pop-ups that open outside the drawer ----
  //
  // Same teleport as the review form above, one step worse. Yotpo renders these pop-ups through a
  // Vue <Teleport to="body">, so they land OUTSIDE this dialog — and a dialog opened with
  // showModal() owns the top layer, which nothing outside it can paint above and nothing outside
  // it can be clicked. Each one looked like nothing happened, and was sitting underneath the
  // drawer the whole time:
  //   - the mobile Filters popup (client-reported 2026-09-11);
  //   - the review photo viewer, opened from a photo on a review (client-reported 2026-09-24).
  //
  // Rather than move Yotpo's node into this dialog — which would put it inside the subtree their
  // own aria-hide-outside directive has just hidden from screen readers, and would pin the
  // viewport-wide photo viewer inside the drawer (its transform is the containing block for fixed
  // children) — the drawer drops out of the top layer for as long as the pop-up is up, and climbs
  // back in when it goes. It stays open and in place throughout (see setModal()), so the pop-up
  // simply opens over it, like a lightbox over a page. Closing it instead, and sliding it back,
  // read as the drawer jumping away and snapping back (client feedback, 2026-09-24).
  var POPUPS = [
    // Closes by `display: none` (v-show); the node stays in the DOM.
    { button: '.yotpo-mobile-filters-btn', popup: '.yotpo-mobile-filters-popup-overlay-container' },
    // Closes by removal (v-if), together with the wrapper Yotpo teleported it in.
    { button: '.yotpo-single-thumbnail', popup: '.yotpo-review-media-modal' }
  ];

  function popupIsOpen(popup) {
    return !!popup && !!popup.isConnected &&
           window.getComputedStyle(popup).display !== 'none';
  }

  function stopPopupWatch() {
    if (!popupWatch) return;
    popupWatch.disconnect();
    popupWatch = null;
  }

  // Move the open drawer between the top layer (showModal) and an ordinary fixed layer (show)
  // without it moving: `is-open` stays on and the slide never runs, and close() and the re-show
  // land in the same task, so no frame is painted in between. .is-lowered (reviews-drawer.css)
  // stacks the ordinary layer above the page and below Yotpo's pop-ups. The close() can still cost
  // the review list its scroll position (focus handling forces a layout while it is hidden), so
  // carry that across.
  function setModal(modal) {
    var scrollTop = mount ? mount.scrollTop : 0;
    dialog.close();
    if (modal) dialog.showModal();
    else dialog.show();
    dialog.classList.toggle('is-lowered', !modal);
    if (mount) mount.scrollTop = scrollTop;
  }

  // The signal to come back is either an attribute change on the pop-up (Filters) or its removal
  // from the page (the photo viewer), so watch the node itself and the child list of every
  // ancestor it could be removed from.
  function watchPopup(popup) {
    stopPopupWatch();
    popupWatch = new window.MutationObserver(function () {
      if (popupIsOpen(popup)) return;
      stopPopupWatch();
      if (dialog.open) setModal(true);
    });
    popupWatch.observe(popup, { attributes: true, attributeFilter: ['style', 'class'] });
    for (var node = popup.parentNode; node; node = node.parentNode) {
      popupWatch.observe(node, { childList: true });
    }
  }

  // Yotpo lazy-loads the filters chunk on the first press, so the popup can be a second or more
  // behind the click — and a popup left over from a previous open is still in the DOM with
  // display:none. Poll for a real one and only then drop the drawer, which also means that if
  // Yotpo ever renames these classes the drawer is left exactly as it behaves today rather than
  // leaving the top layer for a popup that never arrives.
  var POPUP_WAIT_MS = 5000;
  var POPUP_POLL_MS = 100;

  function lowerFor(popupSelector, deadline) {
    // Closed (or closing) while we were waiting — they gave up on the pop-up and left.
    if (!dialog.open || closing) return;
    var popup = document.querySelector(popupSelector);
    if (popupIsOpen(popup)) {
      setModal(false);
      watchPopup(popup);
      // Yotpo's own set-initial-focus ran while this dialog still held the top layer, where the
      // popup was inert and could not take focus. Hand it over now that the dialog has left it.
      var focusable = popup.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusable && typeof focusable.focus === 'function') focusable.focus();
      return;
    }
    if (window.Date.now() > deadline) return;
    window.setTimeout(function () { lowerFor(popupSelector, deadline); }, POPUP_POLL_MS);
  }

  dialog.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    POPUPS.forEach(function (entry) {
      if (target.closest(entry.button)) lowerFor(entry.popup, window.Date.now() + POPUP_WAIT_MS);
    });
  }, true);

  // ESC: native <dialog> fires 'cancel'. Intercept so it runs the animated close
  // and the scroll-lock is always released.
  dialog.addEventListener('cancel', function (event) {
    event.preventDefault();
    closeDrawer();
  });
})();
